import { getLocale } from './locale'

export const formatDateTime = (date: Date) => {
  const dateInstance = new Date(date)
  const formattedDate = dateInstance.toLocaleDateString()
  const formattedTime = dateInstance.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${formattedDate} ${formattedTime}`
}

export const formatCurrency = (amount: number | string | undefined, currency: string = 'USDC') => {
  if (amount === undefined || amount === null || isNaN(Number(amount))) {
    return '-'
  }
  const amountNumber = typeof amount === 'string' ? Number(amount) : amount
  return (
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountNumber) + ` ${currency}`
  )
}

/**
 * Format raw token balance for display
 * @param balance - Raw token balance as string (e.g., "3000000")
 * @param decimals - Token decimals (e.g., 6 for USDC)
 * @param currency - Currency symbol (e.g., "USDC")
 * @returns Formatted balance string (e.g., "3.00 USDC")
 */
export const formatBalance = (balance: string, decimals: number, currency: string): string => {
  const amount = Number(balance) / Math.pow(10, decimals)
  return formatCurrency(amount, currency)
}

export const formatPercentage = (percentage: number): `${string}%` => {
  return `${(percentage * 100).toFixed(2)}%`
}

/**
 * Formats an hash, for instance, a blockchain address (shortened version)
 * @param hash - The full hash to format
 * @param length - Number of characters to show at start and end (default: 6)
 * @returns Formatted address with ellipsis
 */
export function formatHash(hash: string, length: number = 6): string {
  if (length === 0) {
    throw new Error('Length cannot be 0')
  }

  return `${hash.slice(0, 2 + length)}...${hash.slice(-length)}`
}

export function formatBlockchainCurrency(amount: number, currency: string, decimals: number) {
  const locale = getLocale()

  try {
    // Try to use the string currency directly
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount)
  } catch {
    // If currency formatting fails, fallback to decimal format with 6 decimals
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount) + ' ' + currency
  }
}